// Live OGS-streaming avatar, rendered INSIDE the host React-Three-Fiber `<Canvas>`
// scene (Switch Character).
//
// Where OGSModel runs the OGS inference ON-DEVICE, this component is a
// thin host around `createStreamController`: the SERVER runs the CNN/SH inference
// and streams geom + SH over a WebSocket; the browser only untiles + lifts UV→3D
// Gaussians and re-shades SH from the live (host) camera each frame. It shares the
// scene's SparkRenderer (two SparkRenderers double-render), adds the lifted splat
// generators to the host scene, and drives the pipeline from useFrame.
//
// Mounted by App.jsx when in-world streaming mode is active (Switch Character).
// Phase 2 of the in-world integration: renders + orbits; the conversation→rig
// animation bridge (driving sendRig from getCurrentArkit) lands in a later phase.

import { useEffect, useRef } from 'react'
import { useThree, useFrame } from '@react-three/fiber'
import { SparkRenderer } from '@sparkjsdev/spark'
import { SPARK_TRAINING_BLUR } from '../ogs/render/sparkBlur.js'
import { createStreamController } from '../ogs/stream/streamController'
import { faceGraphState } from '../lib/faceGraphState.js'
import { evaluateGraph, updateSMTransitions } from '../lib/GraphEvaluator.js'
import { faceClipPlayer } from '../lib/FaceClipPlayer.js'
import { debugState } from '../lib/debugState.js'
import { currentBlinkWeight } from '../lib/blinkRuntime.js'
import { getCurrentArkit } from '../network/AudioAnimationBuffer.js'
import { composeRigControlArkit, rigControlWeight } from '../lib/rigControlState.js'
import { setVariable, variableValues } from '../lib/animVariables.js'
import { arkitToStreamRig } from '../ogs/rig/arkitToStreamRig.js'
import { gatherFromRigNames } from '../ogs/rig/rigGatherFromNames.js'
import { listOgsAssets, fetchAamFile, cachedFetchAamFile } from '../lib/assetManagerApi.js'
import { buildOGSSceneResolver } from '../ogs/assets/ogsSceneResolver'
import { resolveStreamWsUrl } from '../ogs/stream/resolveStreamWsUrl'

// Mirror of OGSModel.findOrCreateSpark: reuse an existing scene SparkRenderer
// (the env splat / hair / on-device Isaac may own one) — two render the splats
// twice. Only create + add one if none exists. Returns { spark, owned }.
function findOrCreateSpark(scene, gl) {
  let found = null
  scene.traverse((c) => { if (!found && c instanceof SparkRenderer) found = c })
  if (found) return { spark: found, owned: false }
  if (gl.__hairSparkRenderer) {
    if (!gl.__hairSparkRenderer.parent) scene.add(gl.__hairSparkRenderer)
    return { spark: gl.__hairSparkRenderer, owned: false }
  }
  const sr = new SparkRenderer({ renderer: gl, ...SPARK_TRAINING_BLUR })
  scene.add(sr)
  return { spark: sr, owned: true }
}

export default function StreamAvatarModel({ character = 'myra', wsUrl, animationBuffer, onReady, onError, onStage } = {}) {
  const gl = useThree((s) => s.gl)
  const scene = useThree((s) => s.scene)
  const camera = useThree((s) => s.camera)
  const controllerRef = useRef(null)

  // ── conversation→rig bridge (the splat-stream equivalent of HeadModel) ──
  // The server's rig is MetaHuman control-rig space; the conversation streams
  // ARKit-52. We assemble a 52-ARKit frame from the SAME face graph the GLB
  // avatar uses (LiveLink crossfade while talking, idle face clips otherwise,
  // plus blink), convert it to the character's N-control rig via arkitToMh + a
  // per-character gather (verified to match the server's rig_names order), and
  // push it to the server (only on change). Mirrors OGSModel.driveSplatFace,
  // routed to controller.sendRig instead of the on-device splat.
  // Animation-variable debug globals — AvatarModel/OGSModel normally expose
  // these, but neither is mounted in stream mode, while the event graph + tests
  // still read/write variables (e.g. LiveLinkWeight gating live lip-sync). Parity.
  useEffect(() => {
    if (typeof window === 'undefined') return
    window.__SET_VARIABLE__ = setVariable
    window.__VARIABLE_VALUES__ = variableValues
  }, [])

  const gatherRef = useRef(null)
  const arkitRef = useRef(new Float32Array(52))
  const rigRef = useRef(new Float32Array(0))
  const scratch188Ref = useRef(new Float32Array(188))
  const lastRigRef = useRef(new Float32Array(0))
  const haveSentRef = useRef(false)

  // One /ogs-bundle fetch per character, shared by the two effects below
  // (the gather-load and the controller-mount both need the resolved bundle —
  // fetching twice is a redundant round-trip). Cached by character; a switch
  // mints a fresh promise. The gather effect is declared first, so it runs first
  // and populates the cache the controller effect reuses.
  const resolverCacheRef = useRef({ character: null, promise: null })
  const getSceneResolver = (char) => {
    const c = resolverCacheRef.current
    if (c.character === char && c.promise) return c.promise
    // Drop a REJECTED promise from the cache: caching it makes one failed listing
    // permanent for this character, so a momentary asset-manager blip survives every
    // retry until the user switches characters and back.
    const promise = listOgsAssets(char).then(buildOGSSceneResolver).catch((e) => {
      if (resolverCacheRef.current.promise === promise) {
        resolverCacheRef.current = { character: null, promise: null }
      }
      throw e
    })
    resolverCacheRef.current = { character: char, promise }
    return promise
  }

  // Load idle face clips so the state machines can drive an idle expression
  // (same source HeadModel/OGSModel use). Best-effort.
  const faceClipsStartedRef = useRef(false)
  useEffect(() => {
    if (faceClipsStartedRef.current) return
    faceClipsStartedRef.current = true
    faceClipPlayer.loadAll().catch((e) => console.warn('[stream-avatar] face clips load failed (idle stays neutral):', e?.message ?? e))
  }, [])

  // Load the per-character 188→N gather (its order matches the server rig_names)
  // from the character's server-side /ogs bundle — same source as the scene/mesh,
  // no hardcoded per-character map. Absent gather → neutral face.
  useEffect(() => {
    let cancelled = false
    gatherRef.current = null
    haveSentRef.current = false
    ;(async () => {
      let resolver
      try {
        resolver = await getSceneResolver(character)
      } catch (e) {
        console.warn(`[stream-avatar] no streaming bundle for "${character}" — face off:`, e?.message ?? e)
        return
      }
      // Gather source, in precedence order:
      //   1. an explicit rig_gather.json row (a build-time artifact), else
      //   2. derived from the bundle's OWN rig_names.json — the same derivation
      //      the on-device OGSModel path uses. The bundle already declares which
      //      N controls its decoders were trained on and in what order, so a
      //      separate gather row is redundant; most bundles ship without one.
      // Without a gather the ENTIRE ARKit→rig conversion is skipped — that kills
      // prefab/idle face clips and blink, not just lip-sync — so falling back
      // here is what makes a gather-less bundle animate at all.
      try {
        // Small JSON descriptors go through the UNCACHED fetch (same as
        // ogsContentPacks' scene.json): a re-authored gather/rig_names can be
        // the same byte length, which would slip past the cache's size-versioned
        // key and shadow the server indefinitely.
        let g
        if (resolver.gatherUrl) {
          const r = await fetchAamFile(resolver.gatherUrl)
          if (!r.ok) throw new Error(`rig_gather.json ${r.status}`)
          g = await r.json()
          if (!Array.isArray(g)) throw new Error('rig_gather.json is not an array')
        } else {
          const r = await fetchAamFile(resolver.resolveAssetUrl('rig_names.json'))
          if (!r.ok) throw new Error(`rig_names.json ${r.status}`)
          g = gatherFromRigNames(await r.json())
          console.info(`[stream-avatar] gather derived from rig_names.json (${character}): ${g.length} controls`)
        }
        if (cancelled) return
        gatherRef.current = g
        rigRef.current = new Float32Array(g.length)
        lastRigRef.current = new Float32Array(g.length)
        haveSentRef.current = false
      } catch (e) {
        console.error(`[stream-avatar] no rig gather for "${character}" — neutral only (no face animation):`, e?.message ?? e)
      }
    })()
    return () => { cancelled = true }
  }, [character])

  function rigChanged(rig) {
    if (!haveSentRef.current) return true
    const last = lastRigRef.current
    if (last.length !== rig.length) return true
    for (let i = 0; i < rig.length; i++) if (Math.abs(rig[i] - last[i]) > 1e-4) return true
    return false
  }

  function driveStreamFace() {
    const gather = gatherRef.current
    const ctrl = controllerRef.current
    if (!gather || !ctrl || !ctrl.rigDriven) return

    // Evaluate the face graph (same as the GLB avatar) → ARKit-52 frame.
    updateSMTransitions(faceGraphState)
    const { weights: faceWeights } = evaluateGraph(faceGraphState)
    const liveWeight = faceWeights?.['__livelink__'] ?? 0
    faceClipPlayer.writeTimingsInto(debugState.clipTimings)

    const arkit = arkitRef.current
    arkit.fill(0)
    if (liveWeight > 0) {
      // Talking: live A2F ARKit, crossfaded from neutral by the LiveLink weight.
      const live = getCurrentArkit()
      if (live) for (let i = 0; i < 52; i++) arkit[i] = (live[i] ?? 0) * liveWeight
    } else {
      // Idle: blended face clips driven by the state machines (neutral if none).
      const clip = faceClipPlayer.getBlendedWeights(faceWeights ?? {})
      if (clip) for (let i = 0; i < 52; i++) arkit[i] = clip[i] ?? 0
    }
    // Rig Controller (ENG-946): when the face graph's rigController node is
    // active, the Rig Preview sliders replace the frame selected above — or,
    // in passthrough mode, the live stream composed through the locked
    // channels. Blink still overlays after this, matching HeadModel's layering.
    // With no such node the panel can arm direct drive instead (ENG-1027);
    // rigControlWeight owns that precedence.
    const rigWeight = rigControlWeight(faceWeights)
    if (rigWeight > 0) {
      const frame = composeRigControlArkit(getCurrentArkit())
      for (let i = 0; i < 52; i++) arkit[i] = frame[i] * rigWeight
    }

    // Procedural blink on the ARKit blink channels (eyeBlinkLeft=0, eyeBlinkRight=7).
    const blinkGate = faceWeights?.['__blink__'] ?? 0
    if (blinkGate > 0) {
      const b = currentBlinkWeight(blinkGate)
      if (b > 0) { arkit[0] = Math.min(1, arkit[0] + b); arkit[7] = Math.min(1, arkit[7] + b) }
    }

    // ARKit → control-rig, push to the server (only on change).
    const rig = rigRef.current
    arkitToStreamRig(arkit, gather, rig, scratch188Ref.current)
    if (rigChanged(rig)) {
      ctrl.sendRig(rig)
      lastRigRef.current.set(rig)
      haveSentRef.current = true
    }
  }

  // Per render frame: advance the A2F buffer (HeadModel/OGSModel normally do
  // this, but neither is mounted in stream mode), drive the pipeline (queue
  // drain + SH re-shade), then push the conversation-derived rig to the server.
  useFrame(() => {
    animationBuffer?.advancePlayback()
    controllerRef.current?.update(camera.position)
    driveStreamFace()
  })

  useEffect(() => {
    if (!gl || !scene || !camera) return
    let cancelled = false
    let controller = null
    let ownedSpark = null
    let avatarPrevVisible = null
    let added = []
    let camPrev = null
    let controlsPrev = null

    ;(async () => {
      // Scene/mesh source: a first-class streaming character (renderer:'stream')
      // is defined entirely server-side — its scene/mesh bundle lives in the
      // character's /ogs collection, with no bundled public/ copy. So always
      // resolve from the server (shared per-character fetch — see getSceneResolver).
      // No silent fallback: if the character has no bundle, buildOGSSceneResolver
      // throws and init fails loudly (CLAUDE.md).
      //
      // Resolve the bundle BEFORE allocating any GPU resource: a character with
      // no bundle must fail loudly without leaving an orphaned SparkRenderer in
      // the host scene (and lets the loud-failure path be exercised without a
      // GPU). The throw propagates to the outer .catch → onError → App overlay.
      const r = await getSceneResolver(character)
      if (cancelled) return
      const resolveSceneUrl = () => r.sceneUrl
      const resolveAssetUrl = r.resolveAssetUrl
      console.info(`[stream-avatar] scene from server /ogs bundle (${character})`)

      const sparkInfo = findOrCreateSpark(scene, gl)
      ownedSpark = sparkInfo.owned ? sparkInfo.spark : null

      controller = await createStreamController({
        renderer: gl,
        wsUrl: wsUrl || resolveStreamWsUrl(character),
        onStage,
        resolveSceneUrl,
        resolveAssetUrl,
        // Bundle files go through assetManagerApi, which owns the one URL policy
        // (so a bare /api/characters/{slug}/ogs/{file} can't miss the proxy).
        // Split exactly as the on-device /ogs loader does: the big size-versioned
        // mesh binaries are cached (warm reload, no network), the scene.json
        // descriptor is NOT — a same-size edit would otherwise be shadowed by a
        // stale cache entry.
        fetchAsset: cachedFetchAamFile,
        fetchDescriptor: (url) => fetchAamFile(url),
        sizeOf: r.sizeOf,
        onStatus: (m) => console.log('[stream-avatar]', m),
      })
      if (cancelled) {
        controller.dispose()
        if (ownedSpark) { scene.remove(ownedSpark); ownedSpark.dispose?.() }
        return
      }

      added = controller.generators
      for (const g of added) scene.add(g)
      controllerRef.current = controller

      // Frame the host camera to the streamed avatar (meta look_at/radius/fov),
      // same as the standalone demo. World placement/scale tuning is a later phase.
      const meta = controller.meta
      const at = meta.look_at, R = meta.radius
      // Snapshot the host camera/controls so "Stop in-world stream" restores the
      // prior framing (we mutate the shared camera in place below).
      camPrev = { fov: camera.fov, near: camera.near, far: camera.far,
        position: camera.position.clone(), quaternion: camera.quaternion.clone() }
      camera.fov = meta.fov
      camera.near = R * 0.01
      camera.far = R * 100
      camera.position.set(at[0], at[1], at[2] + R)
      camera.lookAt(at[0], at[1], at[2])
      camera.updateProjectionMatrix()
      const controls = typeof window !== 'undefined' ? window.__ORBIT_CONTROLS__ : null
      if (controls) {
        controlsPrev = { target: controls.target.clone(),
          minDistance: controls.minDistance, maxDistance: controls.maxDistance }
        controls.target.set(at[0], at[1], at[2])
        controls.minDistance = R * 0.05
        controls.maxDistance = R * 20
        controls.update()
      }

      // Hide the GLB avatar (Myra) while the stream is on screen — visibility
      // only, restored on unmount. __AVATAR_SCENE__ is the avatar's group.
      const avatar = typeof window !== 'undefined' ? window.__AVATAR_SCENE__ : null
      if (avatar) { avatarPrevVisible = avatar.visible; avatar.visible = false }

      console.info(`[stream-avatar] ready — ${character} streaming in host scene`)
      if (!cancelled) onReady?.()
    })().catch((e) => {
      console.error('[stream-avatar] init failed', e)
      // Loud failure (CLAUDE.md): a stream character defined entirely server-side
      // with no bundle must surface to the App error overlay, not hang on the
      // loading spinner forever. Tear down the half-created owned spark too.
      if (ownedSpark) { scene.remove(ownedSpark); ownedSpark.dispose?.(); ownedSpark = null }
      if (!cancelled) onError?.(e)
    })

    return () => {
      cancelled = true
      controllerRef.current = null
      for (const g of added) scene.remove(g)
      if (controller) controller.dispose()
      if (ownedSpark) { scene.remove(ownedSpark); ownedSpark.dispose?.() }
      const avatar = typeof window !== 'undefined' ? window.__AVATAR_SCENE__ : null
      if (avatar && avatarPrevVisible != null) avatar.visible = avatarPrevVisible
      // Restore the host camera/controls we reframed onto the streamed avatar.
      if (camPrev) {
        camera.fov = camPrev.fov; camera.near = camPrev.near; camera.far = camPrev.far
        camera.position.copy(camPrev.position); camera.quaternion.copy(camPrev.quaternion)
        camera.updateProjectionMatrix()
      }
      const controls = typeof window !== 'undefined' ? window.__ORBIT_CONTROLS__ : null
      if (controls && controlsPrev) {
        controls.target.copy(controlsPrev.target)
        controls.minDistance = controlsPrev.minDistance
        controls.maxDistance = controlsPrev.maxDistance
        controls.update()
      }
    }
  }, [gl, scene, camera, character, wsUrl])

  return null
}
